import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Plus, Calendar as CalendarIcon, Trash2, Clock, MapPin, User, AlertTriangle, CheckCircle, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECURRENCE_LABELS = {
  none: 'Once',
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  quarterly: 'Every 3 months',
  six_monthly: 'Every 6 months',
  annually: 'Annually',
  start_up: 'At each start-up',
};
const RISK_LABELS = {
  no_risk: 'No Risk', very_low_risk: 'Very Low Risk', low_risk: 'Low Risk',
  medium_risk: 'Medium Risk', high_risk: 'High Risk', very_high_risk: 'Very High Risk',
};
const RISK_RECURRENCE = {
  no_risk: 'annually', very_low_risk: 'quarterly', low_risk: 'monthly',
  medium_risk: 'weekly', high_risk: 'start_up', very_high_risk: 'start_up',
};

const Schedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [audits, setAudits] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Form state
  const [auditId, setAuditId] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [scheduledDate, setScheduledDate] = useState(null);
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [reminderDays, setReminderDays] = useState('1');
  const [recurrence, setRecurrence] = useState('none');
  const [riskLevel, setRiskLevel] = useState('no_risk');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // action-assignees is deliberately minimal and company-scoped, so audit
      // creators can schedule work without receiving the full user-management payload.
      const [schedulesRes, auditsRes, usersRes] = await Promise.all([
        axios.get(`${API}/scheduled-audits`),
        axios.get(`${API}/audits`),
        axios.get(`${API}/action-assignees`)
      ]);
      setSchedules(schedulesRes.data);
      setAudits(auditsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!auditId || !assignedTo || !scheduledDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/scheduled-audits`, {
        audit_id: auditId,
        assigned_to: assignedTo,
        // A schedule is a local calendar date, not an instant in time. Sending
        // YYYY-MM-DD avoids UTC/BST conversion moving the audit onto another day.
        scheduled_date: format(scheduledDate, 'yyyy-MM-dd'),
        location: location || null,
        notes: notes || null,
        reminder_days: parseInt(reminderDays),
        recurrence,
        risk_level: riskLevel,
      });
      toast.success('Audit scheduled successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule audit');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this scheduled audit?')) return;
    try {
      await axios.delete(`${API}/scheduled-audits/${id}`);
      toast.success('Scheduled audit deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const seriesAction = async (schedule, action) => {
    const reason = window.prompt(`Reason for ${action === 'active' ? 'resuming' : action} this schedule series:`);
    if (!reason) return;
    try {
      await axios.put(`${API}/scheduled-audits/series/${schedule.series_id || schedule.id}/status`, { status: action, reason });
      toast.success(`Schedule series ${action}`); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not update schedule series'); }
  };

  const skipOccurrence = async (schedule) => {
    const reason = window.prompt('Reason for skipping this occurrence:');
    if (!reason) return;
    try { await axios.post(`${API}/scheduled-audits/${schedule.id}/skip`, { reason }); toast.success('Occurrence skipped'); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Could not skip occurrence'); }
  };

  const triggerStartUp = async (schedule) => {
    try { await axios.post(`${API}/scheduled-audits/series/${schedule.series_id || schedule.id}/trigger`); toast.success('Start-up audit added'); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Could not trigger audit'); }
  };

  const showHistory = async (schedule) => {
    try {
      const response = await axios.get(`${API}/scheduled-audits/series/${schedule.series_id || schedule.id}`);
      const lines = response.data.occurrences.map(item => `${formatDate(item.scheduled_date)} — ${item.status}${item.skip_reason ? ` (${item.skip_reason})` : ''}`);
      window.alert(lines.join('\n') || 'No occurrences');
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not load history'); }
  };

  const editSeries = async (schedule) => {
    const newDate = window.prompt('Next scheduled date (YYYY-MM-DD):', String(schedule.scheduled_date).slice(0, 10));
    if (!newDate) return;
    const newRecurrence = window.prompt('Repeat: weekly, fortnightly, monthly, quarterly, six_monthly, annually or start_up', schedule.recurrence || 'none');
    if (!newRecurrence) return;
    try {
      await axios.put(`${API}/scheduled-audits/series/${schedule.series_id || schedule.id}`, { scheduled_date: newDate, recurrence: newRecurrence });
      toast.success('Schedule series updated'); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not edit schedule series'); }
  };

  const resetForm = () => {
    setAuditId('');
    setAssignedTo('');
    setScheduledDate(null);
    setLocation('');
    setNotes('');
    setReminderDays('1');
    setRecurrence('none');
    setRiskLevel('no_risk');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'overdue':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertTriangle className="w-3 h-3 mr-1" />Overdue</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const raw = String(dateStr).slice(0, 10);
    const [year, month, day] = raw.split('-').map(Number);
    const date = year && month && day ? new Date(year, month - 1, day) : new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const pendingCount = schedules.filter(s => s.status === 'pending').length;
  const overdueCount = schedules.filter(s => s.status === 'overdue').length;

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Audit Schedule</h1>
          <p className="text-muted-foreground mt-1">Schedule and manage upcoming audits</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="schedule-audit-btn"><Plus className="w-4 h-4 mr-2" />Schedule Audit</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto">
            <DialogHeader><DialogTitle>Schedule an Audit</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Audit Template *</Label>
                <Select value={auditId} onValueChange={setAuditId}>
                  <SelectTrigger data-testid="schedule-audit-select"><SelectValue placeholder="Select an audit" /></SelectTrigger>
                  <SelectContent>{audits.map(audit => <SelectItem key={audit.id} value={audit.id}>{audit.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Assign To *</Label>
                <Select value={assignedTo} onValueChange={setAssignedTo}>
                  <SelectTrigger data-testid="schedule-user-select"><SelectValue placeholder="Select a user" /></SelectTrigger>
                  <SelectContent>
                    {users.map(user => <SelectItem key={user.id} value={user.id}>{user.name} ({user.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{recurrence !== 'none' ? 'First Scheduled Date *' : 'Scheduled Date *'}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="schedule-date-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {scheduledDate ? format(scheduledDate, 'dd/MM/yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={scheduledDate}
                      onSelect={setScheduledDate}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Risk level</Label>
                <Select value={riskLevel} onValueChange={(value) => { setRiskLevel(value); setRecurrence(RISK_RECURRENCE[value]); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(RISK_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Suggested frequency: {RECURRENCE_LABELS[RISK_RECURRENCE[riskLevel]]}.</p>
              </div>

              <div className="space-y-2">
                <Label>Repeat</Label>
                <Select value={recurrence} onValueChange={setRecurrence}>
                  <SelectTrigger data-testid="schedule-recurrence"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Does not repeat</SelectItem>
                    <SelectItem value="weekly">Every week</SelectItem>
                    <SelectItem value="fortnightly">Every 2 weeks</SelectItem>
                    <SelectItem value="monthly">Every month</SelectItem>
                    <SelectItem value="quarterly">Every 3 months</SelectItem>
                    <SelectItem value="six_monthly">Every 6 months</SelectItem>
                    <SelectItem value="annually">Every year</SelectItem>
                    <SelectItem value="start_up">At each start-up (manually triggered)</SelectItem>
                  </SelectContent>
                </Select>
                {recurrence !== 'none' && (
                  <p className="text-xs text-muted-foreground">
                    Each occurrence is completed by any matching company audit run during its Monday–Sunday week.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Location (Optional)</Label>
                <Input placeholder="e.g., Main Kitchen, Site A" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="schedule-location" />
              </div>

              <div className="space-y-2">
                <Label>Reminder (days before)</Label>
                <Select value={reminderDays} onValueChange={setReminderDays}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">On the day</SelectItem>
                    <SelectItem value="1">1 day before</SelectItem>
                    <SelectItem value="2">2 days before</SelectItem>
                    <SelectItem value="3">3 days before</SelectItem>
                    <SelectItem value="7">1 week before</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <Mail className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  The assigned auditor will be emailed at this point, unless they have disabled scheduled-audit emails in My Account.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea placeholder="Any special instructions..." value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="schedule-notes" />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1" data-testid="schedule-submit-btn">Schedule</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center"><Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" /></div><div><p className="text-2xl font-bold">{pendingCount}</p><p className="text-sm text-muted-foreground">Pending Audits</p></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-lg bg-red-100 dark:bg-red-900/20 flex items-center justify-center"><AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" /></div><div><p className="text-2xl font-bold">{overdueCount}</p><p className="text-sm text-muted-foreground">Overdue</p></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center"><CheckCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-400" /></div><div><p className="text-2xl font-bold">{schedules.filter(s => s.status === 'completed').length}</p><p className="text-sm text-muted-foreground">Completed</p></div></div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Scheduled Audits</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="flex items-center gap-4"><Skeleton className="h-10 w-10 rounded-full" /><div className="flex-1 space-y-2"><Skeleton className="h-4 w-1/3" /><Skeleton className="h-3 w-1/4" /></div><Skeleton className="h-6 w-20" /></div>)}</div>
          ) : schedules.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Audit</TableHead><TableHead>Assigned To</TableHead><TableHead>Date</TableHead><TableHead>Risk</TableHead><TableHead>Repeat</TableHead><TableHead>Location</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {schedules.map((schedule) => (
                    <TableRow key={schedule.id} data-testid={`schedule-row-${schedule.id}`}>
                      <TableCell className="font-medium">{schedule.audit_name}</TableCell>
                      <TableCell><div className="flex items-center gap-2"><User className="w-4 h-4 text-muted-foreground" />{schedule.assigned_to_name}</div></TableCell>
                      <TableCell><div className="flex items-center gap-2"><CalendarIcon className="w-4 h-4 text-muted-foreground" />{formatDate(schedule.scheduled_date)}</div></TableCell>
                      <TableCell><Badge variant="outline">{RISK_LABELS[schedule.risk_level] || 'No Risk'}</Badge></TableCell>
                      <TableCell>{!schedule.recurrence || schedule.recurrence === 'none' ? 'Once' : <Badge variant="outline">{RECURRENCE_LABELS[schedule.recurrence] || schedule.recurrence}</Badge>}</TableCell>
                      <TableCell>{schedule.location ? <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" />{schedule.location}</div> : '-'}</TableCell>
                      <TableCell>{getStatusBadge(schedule.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 flex-wrap">
                        <Button variant="ghost" size="sm" onClick={() => showHistory(schedule)}>History</Button>
                        {schedule.series_status !== 'ended' && <Button variant="ghost" size="sm" onClick={() => editSeries(schedule)}>Edit</Button>}
                        {schedule.recurrence === 'start_up' && schedule.series_status !== 'ended' && <Button variant="ghost" size="sm" onClick={() => triggerStartUp(schedule)}>Trigger</Button>}
                        {['pending', 'overdue'].includes(schedule.status) && <Button variant="ghost" size="sm" onClick={() => skipOccurrence(schedule)}>Skip</Button>}
                        {schedule.series_status === 'paused' ? <Button variant="ghost" size="sm" onClick={() => seriesAction(schedule, 'active')}>Resume</Button> : schedule.series_status !== 'ended' && <Button variant="ghost" size="sm" onClick={() => seriesAction(schedule, 'paused')}>Pause</Button>}
                        {schedule.series_status !== 'ended' && <Button variant="ghost" size="sm" onClick={() => seriesAction(schedule, 'ended')}>End</Button>}
                        {schedule.status !== 'completed' && <Button variant="ghost" size="sm" onClick={() => handleDelete(schedule.id)} className="text-destructive hover:text-destructive" data-testid={`delete-schedule-${schedule.id}`}><Trash2 className="w-4 h-4" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12"><CalendarIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" /><p className="text-muted-foreground mb-4">No scheduled audits</p><Button onClick={() => setDialogOpen(true)}><Plus className="w-4 h-4 mr-2" />Schedule Your First Audit</Button></div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Schedule;
